import i18n from '../i18n/config';
import exportsApi from '../api/exports.api';
import { QToastProps } from '@qualio/ui-components';
import { wait } from './asyncUtils';
import { FileReady, PreparingFileForDownload, FailedToExportTheFile } from '../displayStrings';

export interface ExportUtilDefinition {
  showToast: (options: QToastProps) => void;
  displayExportComplete(payload: string, exportsId: string): void;
  exportStatus(companyId: number, exportsId: string): void;
  displayPreparingForDownload(exportsId: string): void;
  displayExportStatus(companyId: number, exportsId: string): void;
  displayErrorTryingToExport(exportsId: string): void;
}

export default class ExportUtil implements ExportUtilDefinition {
  private readonly useSearchExport: boolean;

  constructor(
    public showToast: (options: QToastProps) => void,
    useSearchExport = false,
  ) {
    this.useSearchExport = useSearchExport;
  }

  displayExportComplete = (payload: string, exportsId: string) => {
    this.showToast({
      title: FileReady,
      description: (
        <span>
          {i18n.t('common.notifications.fileReadyToDownloadPrefix')}{' '}
          <a style={{ color: '#0069FF' }} href={payload}>
            {i18n.t('common.notifications.fileReadyToDownloadLinkText')}
          </a>
        </span>
      ),
      duration: null,
      status: 'success',
      id: exportsId,
      replace: true,
      'data-cy': 'qe-export-ready-success-toast',
    });
  };

  exportStatus = (companyId: number, exportsId: string) => {
    this.retryWithDelay(companyId, exportsId, 300);
  };

  retryWithDelay = async (companyId: number, exportsId: string, retries: number): Promise<void> => {
    try {
      const fetchFn = this.useSearchExport ? exportsApi.getSearchExport : exportsApi.getExport;
      const exportStatusResponse = await fetchFn(companyId, exportsId);
      if (exportStatusResponse?.ready === true) {
        if (exportStatusResponse?.successful) {
          this.displayExportComplete(exportStatusResponse?.payload as string, exportsId);
        } else {
          this.displayErrorTryingToExport(exportsId);
        }
        return;
      }
      if (retries <= 0) {
        this.displayErrorTryingToExport(exportsId);
        return;
      }
      await wait(1000);
      return this.retryWithDelay(companyId, exportsId, retries - 1);
    } catch (err) {
      console.log(err);
      this.displayErrorTryingToExport(exportsId);
    }
  };

  displayPreparingForDownload = (exportsId: string) => {
    this.showToast({
      title: PreparingFileForDownload,
      description: i18n.t('common.notifications.wellLetYouKnowWhenFileIsReady'),
      duration: null,
      status: 'info',
      id: exportsId,
      replace: true,
    });
  };

  displayExportStatus = (companyId: number, exportsId: string) => {
    this.displayPreparingForDownload(exportsId);
    this.exportStatus(companyId, exportsId);
  };

  displayErrorTryingToExport = (exportsId: string) => {
    this.showToast({
      title: i18n.t('common.notifications.error'),
      description: FailedToExportTheFile,
      duration: 5000,
      status: 'error',
      id: exportsId,
      replace: true,
    });
  };
}
